import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, Pencil, Trash2, Target, ShieldCheck, Loader2, X, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PROFESSIONAL_ROLES, CARE_ROLES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const STATUS_COLOR = {
  red: { bg: "bg-red-500/15", tx: "text-red-300", dot: "#ef4444" },
  orange: { bg: "bg-amber-500/15", tx: "text-amber-300", dot: "#f59e0b" },
  lightgreen: { bg: "bg-lime-500/15", tx: "text-lime-300", dot: "#84cc16" },
  green: { bg: "bg-emerald-500/15", tx: "text-emerald-300", dot: "#10b981" },
  neutral: { bg: "bg-white/[0.06]", tx: "text-slate-300", dot: "#94a3b8" },
};
const GOAL_ST_COLOR = { a_faire: "text-slate-300 bg-white/10", en_cours: "text-sky-300 bg-sky-500/15", atteint: "text-emerald-300 bg-emerald-500/15", abandonne: "text-slate-400 bg-white/5 line-through" };
const PRIO_COLOR = { basse: "text-slate-300 bg-white/10", moyenne: "text-amber-300 bg-amber-500/15", haute: "text-red-300 bg-red-500/15" };
const ACTION_ST_COLOR = { a_faire: "border-slate-500 text-slate-300", en_cours: "border-sky-400 text-sky-300", realisee: "border-emerald-400 text-emerald-300", non_realisee: "border-red-400 text-red-300" };
const GOAL_STATUSES = ["a_faire", "en_cours", "atteint", "abandonne"];
const ACTION_STATUSES = ["a_faire", "en_cours", "realisee", "non_realisee"];
const PRIORITIES = ["basse", "moyenne", "haute"];
const ASSIGN_ROLES = [...CARE_ROLES, ...PROFESSIONAL_ROLES];

const inputCls = "bg-white/[0.04] border-white/10";
const selectCls = "w-full h-9 rounded-md bg-white/[0.04] border border-white/10 text-sm px-2 text-slate-100";

function Ring({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const r = 26, c = 2 * Math.PI * r;
  return (
    <div className="relative h-16 w-16 shrink-0" data-testid="pp-progress-ring">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#ffffff1a" strokeWidth="6" />
        <circle cx="32" cy="32" r={r} fill="none" stroke="#14b8a6" strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-100">{done}/{total}</div>
    </div>
  );
}

export default function PPDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [domains, setDomains] = useState([]);
  const [otypes, setOtypes] = useState([]);
  const [support, setSupport] = useState({});
  const [loading, setLoading] = useState(true);
  const [goalDlg, setGoalDlg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, sup, ot] = await Promise.all([api.get(`/pp/residents/${id}`), api.get("/pp/domains"), api.get(`/pp/residents/${id}/support`), api.get("/pp/objective-types")]);
      setSheet(s.data); setDomains(d.data.domains || []); setOtypes(ot.data.objective_types || []);
      const m = {}; (sup.data.support || []).forEach((x) => { m[x.objective_id] = x.count; });
      setSupport(m);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const canEdit = sheet?.can_edit;
  const canToggleAction = (a) => canEdit || a.assignee_role === user?.role ||
    (a.assignee_name && a.assignee_name.trim().toLowerCase() === (user?.name || "").trim().toLowerCase());

  const setActionStatus = async (gid, aid, status) => {
    try {
      const { data } = await api.patch(`/pp/goals/${gid}/actions/${aid}`, { status });
      setSheet((s) => ({ ...s, goals: s.goals.map((g) => (g.id === gid ? data : g)) }));
      toast.success(t("pp.actionUpdated"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const saveGoal = async () => {
    setBusy(true);
    const g = goalDlg;
    const payload = {
      domain: g.domain || "", title: g.title || "", status: g.status || "a_faire",
      priority: g.priority || "moyenne", due_date: g.due_date || null,
      objective_type_id: g.objective_type_id || null,
      actions: (g.actions || []).filter((a) => (a.text || "").trim()).map((a) => ({
        id: a.id, text: a.text, assignee_role: a.assignee_role || "", assignee_name: a.assignee_name || "",
        due_date: a.due_date || null, status: a.status || "a_faire",
      })),
    };
    try {
      if (g.id) await api.put(`/pp/goals/${g.id}`, payload);
      else await api.post(`/pp/residents/${id}/goals`, payload);
      toast.success(t("pp.goalSaved")); setGoalDlg(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  const deleteGoal = async (gid) => {
    if (!window.confirm(t("pp.confirmDeleteGoal"))) return;
    try { await api.delete(`/pp/goals/${gid}`); toast.success(t("pp.goalDeleted")); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const newGoal = () => setGoalDlg({ domain: "", title: "", status: "a_faire", priority: "moyenne", due_date: "", actions: [], objective_type_id: null });
  const editGoal = (g) => setGoalDlg({
    id: g.id, domain: g.domain || "", title: g.title || "", status: g.status || "a_faire",
    priority: g.priority || "moyenne", due_date: (g.due_date || "").slice(0, 10), objective_type_id: g.objective_type_id || null,
    actions: (g.actions || []).map((a) => ({ ...a, due_date: (a.due_date || "").slice(0, 10) })),
  });
  const addActionLine = () => setGoalDlg((d) => ({ ...d, actions: [...(d.actions || []), { text: "", assignee_role: "", assignee_name: "", due_date: "", status: "a_faire" }] }));
  const updActionLine = (i, patch) => setGoalDlg((d) => ({ ...d, actions: d.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }));
  const rmActionLine = (i) => setGoalDlg((d) => ({ ...d, actions: d.actions.filter((_, idx) => idx !== i) }));

  const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  if (loading) return <div className="min-h-screen bg-[#0a1721] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal-400" /></div>;
  if (!sheet) return <div className="min-h-screen bg-[#0a1721] text-slate-300 p-8">{t("pp.empty")}</div>;

  const col = STATUS_COLOR[sheet.color] || STATUS_COLOR.neutral;
  const res = sheet.resident || {};

  return (
    <div className="min-h-screen bg-[#0a1721] text-slate-100 p-4 sm:p-6" data-testid="pp-detail-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate("/suivi-pp")} data-testid="pp-back" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal-300"><ArrowLeft className="h-4 w-4" />{t("pp.backToList")}</button>
          <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pp/residents/${id}/pdf`, "_blank")} data-testid="pp-export-pdf"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/15 px-3.5 py-1.5 text-sm font-semibold text-slate-100 transition"><FileDown className="h-4 w-4" />{t("pp.pdfExport")}</button>
        </div>

        {/* En-tête habitant */}
        <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 mb-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Ring done={sheet.goals_done || 0} total={sheet.goals_total || 0} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex flex-wrap items-end gap-4" data-testid="pp-detail-name">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("pp.fLast")}</span>
                    <div className="mt-0.5 h-8 w-40 rounded-md border border-dashed border-slate-400 bg-transparent" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("pp.fFirst")}</span>
                    <div className="mt-0.5 h-8 w-40 rounded-md border border-dashed border-slate-400 bg-transparent" />
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${col.bg} ${col.tx}`}>
                  <span className="h-2 w-2 rounded-full" style={{ background: col.dot }} />{t(`pp.status.${sheet.label_key}`, { defaultValue: sheet.label_key })}
                </span>
              </div>
              <p className="text-sm text-slate-400 mt-1">{res.room ? `${t("pp.col.room")} ${res.room} · ` : ""}{t("pp.progressLabel")} : {sheet.goals_done || 0}/{sheet.goals_total || 0}</p>
            </div>
          </div>
        </div>

        {/* Bloc administratif */}
        <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 mb-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-3">{t("pp.infoSection")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <Info label={t("pp.col.ref1")} value={sheet.referent1 || "—"} />
            <Info label={t("pp.col.ref2")} value={sheet.referent2 || "—"} />
            <Info label={t("pp.col.attestation")} value={sheet.attestation_signed ? <ShieldCheck className="h-4 w-4 text-emerald-400 inline" /> : "—"} />
            <Info label={t("pp.col.validation")} value={fmtD(sheet.first_validation_date)} />
            <Info label={t("pp.col.lastUpdate")} value={fmtD(sheet.last_update_date)} />
            <Info label={t("pp.col.nextUpdate")} value={fmtD(sheet.due_date || sheet.next_update_date)} />
            <Info label={t("pp.col.meeting")} value={fmtD(sheet.next_meeting_date)} />
            {sheet.comments ? <div className="col-span-2 sm:col-span-3"><Info label={t("pp.col.comments")} value={sheet.comments} /></div> : null}
          </div>
        </div>

        {/* Objectifs & plans d'action */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2"><Target className="h-5 w-5 text-teal-400" />{t("pp.goalsSection")}</h2>
          {canEdit && <Button onClick={newGoal} data-testid="pp-add-goal" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold"><Plus className="h-4 w-4 mr-1" />{t("pp.addGoal")}</Button>}
        </div>

        {(sheet.goals || []).length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 py-14 text-center text-slate-500" data-testid="pp-no-goals">{t("pp.noGoals")}</div>
        ) : (
          <div className="space-y-4">
            {sheet.goals.map((g) => (
              <div key={g.id} className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5" data-testid={`pp-goal-${g.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {g.domain && <span className="text-xs font-semibold rounded-full bg-teal-500/15 text-teal-300 px-2.5 py-0.5">{g.domain}</span>}
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${PRIO_COLOR[g.priority] || PRIO_COLOR.moyenne}`}>{t(`pp.priorityLbl.${g.priority}`, { defaultValue: g.priority })}</span>
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${GOAL_ST_COLOR[g.status] || ""}`}>{t(`pp.goalStatusLbl.${g.status}`, { defaultValue: g.status })}</span>
                      {g.due_date && <span className="text-xs text-slate-400">· {t("pp.gDueDate")} {fmtD(g.due_date)}</span>}
                    </div>
                    <h3 className="font-semibold text-slate-100">{g.title}</h3>
                    {(support[g.id] || 0) > 0 && (
                      <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-teal-500/10 text-teal-300 px-2.5 py-0.5 text-xs font-medium" data-testid={`pp-support-${g.id}`}>
                        <Target className="h-3 w-3" />{t("pp.support.supported", { count: support[g.id] })}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => editGoal(g)} data-testid={`pp-edit-goal-${g.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-sky-300 hover:bg-white/5"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => deleteGoal(g.id)} data-testid={`pp-del-goal-${g.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-300 hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>

                {(g.actions || []).length > 0 && (
                  <div className="mt-3 border-t border-white/5 pt-3 space-y-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t("pp.gActions")}</p>
                    {g.actions.map((a) => {
                      const toggleable = canToggleAction(a);
                      return (
                        <div key={a.id} className="flex items-center gap-2 flex-wrap text-sm" data-testid={`pp-action-${a.id}`}>
                          <select value={a.status} disabled={!toggleable} onChange={(e) => setActionStatus(g.id, a.id, e.target.value)}
                            data-testid={`pp-action-status-${a.id}`}
                            className={`h-7 rounded-md bg-transparent border text-xs px-1.5 ${ACTION_ST_COLOR[a.status] || "border-slate-500 text-slate-300"} ${toggleable ? "cursor-pointer" : "opacity-70 cursor-not-allowed"}`}>
                            {ACTION_STATUSES.map((s) => <option key={s} value={s} className="bg-[#0f2130] text-slate-100">{t(`pp.actionStatusLbl.${s}`)}</option>)}
                          </select>
                          <span className={`flex-1 min-w-[120px] ${a.status === "realisee" ? "text-slate-400 line-through" : "text-slate-200"}`}>{a.text}</span>
                          {a.assignee_role && <span className="text-xs text-slate-400">{t(`enums.role.${a.assignee_role}`, { defaultValue: a.assignee_role })}</span>}
                          {a.assignee_name && <span className="text-xs text-slate-500">({a.assignee_name})</span>}
                          {a.due_date && <span className="text-xs text-slate-500">· {fmtD(a.due_date)}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog Objectif */}
      <Dialog open={!!goalDlg} onOpenChange={(o) => { if (!o) setGoalDlg(null); }}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pp-goal-dialog">
          <DialogHeader><DialogTitle>{goalDlg?.id ? t("pp.editGoal") : t("pp.addGoal")}</DialogTitle></DialogHeader>
          {goalDlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Fld label={t("pp.gDomain")}>
                  <select value={goalDlg.domain} onChange={(e) => setGoalDlg({ ...goalDlg, domain: e.target.value })} className={selectCls} data-testid="pp-goal-domain">
                    <option value="" className="bg-[#0f2130]">{t("pp.selectDomain")}</option>
                    {domains.map((d) => <option key={d.id || d.label} value={d.label} className="bg-[#0f2130]">{d.label}</option>)}
                  </select>
                </Fld>
                <Fld label={t("pp.gPriority")}>
                  <select value={goalDlg.priority} onChange={(e) => setGoalDlg({ ...goalDlg, priority: e.target.value })} className={selectCls} data-testid="pp-goal-priority">
                    {PRIORITIES.map((p) => <option key={p} value={p} className="bg-[#0f2130]">{t(`pp.priorityLbl.${p}`)}</option>)}
                  </select>
                </Fld>
              </div>
              <Fld label={t("pp.gTitle")}>
                <ObjectiveAutocomplete
                  value={goalDlg.title}
                  otypes={otypes}
                  onText={(txt) => setGoalDlg((d) => ({ ...d, title: txt, objective_type_id: null }))}
                  onPick={(o) => setGoalDlg((d) => ({ ...d, title: o.label, domain: o.domain || d.domain, objective_type_id: o.id }))}
                />
                {goalDlg.objective_type_id ? (
                  <p className="text-[11px] text-teal-300/80 mt-1" data-testid="pp-goal-type-hint">{t("pp.objTypes.linkedHint")}</p>
                ) : goalDlg.title ? (
                  <p className="text-[11px] text-slate-500 mt-1" data-testid="pp-goal-free-hint">{t("pp.objTypes.freeHint")}</p>
                ) : null}
              </Fld>
              <div className="grid grid-cols-2 gap-3">
                <Fld label={t("pp.gStatus")}>
                  <select value={goalDlg.status} onChange={(e) => setGoalDlg({ ...goalDlg, status: e.target.value })} className={selectCls} data-testid="pp-goal-status">
                    {GOAL_STATUSES.map((s) => <option key={s} value={s} className="bg-[#0f2130]">{t(`pp.goalStatusLbl.${s}`)}</option>)}
                  </select>
                </Fld>
                <Fld label={t("pp.gDueDate")}>
                  <Input type="date" value={goalDlg.due_date || ""} onChange={(e) => setGoalDlg({ ...goalDlg, due_date: e.target.value })} className={inputCls} />
                </Fld>
              </div>

              <div className="border-t border-white/10 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-200">{t("pp.gActions")}</span>
                  <button onClick={addActionLine} data-testid="pp-add-action" className="text-xs inline-flex items-center gap-1 text-teal-300 hover:text-teal-200"><Plus className="h-3.5 w-3.5" />{t("pp.addActionLine")}</button>
                </div>
                {(goalDlg.actions || []).length === 0 && <p className="text-xs text-slate-500">{t("pp.noActions")}</p>}
                <div className="space-y-2">
                  {(goalDlg.actions || []).map((a, i) => (
                    <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-2.5 space-y-2" data-testid={`pp-action-line-${i}`}>
                      <div className="flex items-start gap-2">
                        <Input value={a.text} onChange={(e) => updActionLine(i, { text: e.target.value })} placeholder={t("pp.aText")} className={`${inputCls} flex-1`} data-testid={`pp-action-text-${i}`} />
                        <button onClick={() => rmActionLine(i)} className="p-1.5 text-slate-400 hover:text-red-300"><X className="h-4 w-4" /></button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <select value={a.assignee_role || ""} onChange={(e) => updActionLine(i, { assignee_role: e.target.value })} className={selectCls}>
                          <option value="" className="bg-[#0f2130]">{t("pp.unassigned")}</option>
                          {ASSIGN_ROLES.map((r) => <option key={r} value={r} className="bg-[#0f2130]">{t(`enums.role.${r}`, { defaultValue: r })}</option>)}
                        </select>
                        <Input value={a.assignee_name || ""} onChange={(e) => updActionLine(i, { assignee_name: e.target.value })} placeholder={t("pp.aAssigneeName")} className={inputCls} />
                        <Input type="date" value={a.due_date || ""} onChange={(e) => updActionLine(i, { due_date: e.target.value })} className={inputCls} />
                        <select value={a.status || "a_faire"} onChange={(e) => updActionLine(i, { status: e.target.value })} className={selectCls}>
                          {ACTION_STATUSES.map((s) => <option key={s} value={s} className="bg-[#0f2130]">{t(`pp.actionStatusLbl.${s}`)}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGoalDlg(null)}>{t("pp.cancel")}</Button>
            <Button onClick={saveGoal} disabled={busy || !goalDlg?.title} data-testid="pp-save-goal" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold">{busy ? t("pp.saving") : t("pp.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs text-slate-500">{label}</div><div className="text-slate-200">{value}</div></div>;
}
function Fld({ label, children }) {
  return <div><label className="text-xs font-semibold text-slate-400 block mb-1">{label}</label>{children}</div>;
}

const _norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
// Combobox pré-rempli : liste déroulante des objectifs types (avec recherche) + saisie libre possible.
function ObjectiveAutocomplete({ value, otypes, onText, onPick }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const q = _norm(value);
  const filtered = otypes.filter((o) => !q || _norm(o.label).includes(q)).slice(0, 60);
  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value}
        onChange={(e) => { onText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={t("pp.objTypes.pickPlaceholder")}
        className="bg-white/[0.04] border-white/10"
        data-testid="pp-goal-title"
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-white/10 bg-[#0f2130] shadow-xl" data-testid="pp-goal-type-list">
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onPick(o); setOpen(false); }}
              data-testid={`pp-goal-type-opt-${o.id}`}
              className="w-full text-left px-3 py-2 hover:bg-white/[0.06] border-b border-white/5 last:border-0"
            >
              <div className="text-sm text-slate-100">{o.label}</div>
              {o.domain && <div className="text-[11px] text-teal-300/70">{o.domain}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

